<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";

import appConfig from "@/app.config";
import tatApiSvc from "@/services/tat-api";
import provinceSvc from "@/services/province";
import Multiselect from "@vueform/multiselect";
import { DateTime } from "luxon";

/**
 * Customers component
 */
export default {
  page: {
    title: "ATP List",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: "ATP List",
      role:
        localStorage.getItem("userInfo") !== null
          ? JSON.parse(localStorage.getItem("userInfo")).role
          : "user",
      items: [
        {
          text: "Third Party",
          href: "#",
        },
        {
          text: "ATP List",
          active: true,
        },
      ],
      columns: [
        {
          label: "",
          field: "lineNumber",
          sortable: false,
        },
        {
          label: "ID",
          field: "id",
          sortable: false,
        },
        {
          field: "documentId",
          hidden: true,
        },
        {
          label: "Name",
          field: "eventFestivalName",
          sortable: false,
        },
        {
          label: "Province",
          field: "province",
          sortable: false,
        },
        {
          label: "Start Date",
          field: "eventStartDate",
          type: "Date",
          sortable: false,
        },
        {
          label: "End Date",
          field: "eventEndDate",
          type: "Date",
          sortable: false,
        },
        {
          label: "Created Date",
          field: "createdAt",
          type: "Date",
          sortable: false,
        },
        {
          label: "Last Updated",
          field: "updatedAt",
          type: "Date",
          sortable: false,
        },
        {
          label: "Clone Status",
          field: "cloneStatus",
          sortable: false,
        },
        {
          label: "Action",
          field: "button",
          html: true,
          sortable: false,
          tdClass: "text-center",
          thClass: "text-center",
        },
      ],
      rows: [],
      provincesOption: [],
      totalRows: 0,
      table: {
        page: 1,
        perPage: 50,
        type: "",
        status: "",
        sort: {
          by: "",
          type: "",
        },
        search: "",
      },
      typingTimer: null,
      filter: {
        keyword: "",
        province: "",
      },
    };
  },
  mounted() {
    this.get();
    this.getProvince();
  },
  methods: {
    onPageChange(params) {
      this.table.page = params.currentPage;
      this.table.perPage = params.currentPerPage;
      this.get();
    },
    onSortChange(params) {
      params.forEach((v) => {
        this.table.sort.by = v.type !== "none" ? v.field : "";
        this.table.sort.type = v.type !== "none" ? v.type : "";
      });
      this.table.page = 1;
      this.get();
    },
    onColumnFilter(params) {
      for (const [field, value] of Object.entries(params.columnFilters)) {
        if (field === "statusCode") {
          this.table.status = value;
        } else if (field === "type") {
          this.table.type = value;
        }
      }
      this.table.page = 1;
      this.get();
    },
    getProvince() {
      const region = "";
      provinceSvc.getDropdown(region).then((resp) => {
        this.provincesOption = resp.data.data.map((province) => {
          return { value: province.textTh, label: province.text };
        });
      });
    },
    handleSearch() {
      clearTimeout(this.typingTimer);

      this.typingTimer = setTimeout(() => {
        this.table.page = 1;
        this.get();
      }, 1500);
    },
    search() {
      this.table.page = 1;
      this.get();
    },
    clearFilter() {
      this.filter.keyword = "";
      this.filter.province = "";
      this.table.page = 1;
      this.get();
    },
    get() {
      tatApiSvc
        .getAllApt(this.table.page, this.table.perPage, this.filter.keyword, this.filter.province)
        .then(async (resp) => {
          if (resp.data.data === null) {
            this.rows = [];
            this.totalRows = 0;
            return;
          }

          for (let v of resp.data.data.data) {
            v.dateCreated = v.dateCreated
              ? DateTime.fromISO(v.dateCreated).toFormat("yyyy-MM-dd")
              : "";
            v.dateUpdated = v.dateUpdated
              ? DateTime.fromISO(v.dateUpdated).toFormat("yyyy-MM-dd")
              : "";
          }

          this.rows = resp.data.data.data;
          this.totalRows = resp.data.data.pagination.total;
        });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="mb-3 d-flex flex-wrap gap-2 align-items-end">
              <div style="flex: 1; min-width: 250px;">
                <label class="form-label">Name</label>
                <input
                  v-model="filter.keyword"
                  type="text"
                  class="form-control"
                  placeholder="Name"
                  @keyup.enter="search"
                />
              </div>
              <div style="flex: 1; min-width: 250px;">
                <label class="form-label">Province</label>
                <Multiselect
                  v-model="filter.province"
                  mode="single"
                  :searchable="true"
                  :options="provincesOption"
                  placeholder="-- All --"
                />
              </div>
              <div class="d-flex gap-2">
                <b-button variant="primary" @click="search">
                  <i class="mdi mdi-magnify align-middle"></i> Search
                </b-button>
                <b-button variant="secondary" @click="clearFilter">
                  <i class="mdi mdi-close align-middle"></i> Clear
                </b-button>
              </div>
            </div>
          <div class="table-responsive">
              <vue-good-table
                :pagination-options="{
                  enabled: true,
                  perPage: 50,
                  mode: 'pages',
                  dropdownAllowAll: false,
                  perPageDropdown: [50, 100],
                }"
                v-on:per-page-change="onPageChange"
                v-on:page-change="onPageChange"
                v-on:search="onSearch"
                v-on:sort-change="onSortChange"
                v-on:column-filter="onColumnFilter"
                styleClass="tableOne vgt-table striped"
                :columns="columns"
                :rows="rows"
                :totalRows="totalRows"
              >
                <template #table-row="props">
                  <span v-if="props.column.field == 'lineNumber'">
                    <th class="line-numbers">
                      {{ (table.page - 1) * table.perPage + props.index + 1 }}
                    </th>
                  </span>
                  <span v-else-if="props.column.field == 'button'">
                    <b-button
                      size="sm"
                      variant="primary"
                      router-link
                      :to="`manage-apt?uuid=${props.formattedRow.documentId}`"
                    >
                      <i class="mdi mdi-eye font-size-16 align-middle"></i>
                      View
                    </b-button>
                  </span>
                  <span v-else-if="props.column.field == 'cloneStatus'">
                    <b-badge
                      v-if="['already'].includes(props.row.cloneStatus)"
                      variant="success"
                      >Already</b-badge
                    >
                    <div v-if="['warning'].includes(props.row.cloneStatus)">
                      <b-badge variant="success">Already</b-badge>
                      <div>
                        <i class="bx bx bxs-error-circle text-danger"
                          >ข้อมูลมีการเปลี่ยนแปลง</i
                        >
                      </div>
                    </div>
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- end row -->
  </Layout>
</template>

<style>
th.line-numbers {
  border-bottom: none !important;
}

.vgt-wrap__footer .footer__navigation__page-info__current-entry {
  width: 100px !important;
}
</style>
